import React, { useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  AtSign,
  BadgeCheck,
  CalendarDays,
  Copy,
  Mail,
  MapPin,
  Phone,
  ShieldAlert
} from 'lucide-react';
import ConfirmVerify from './VerifyAccount';

const initials = (customer) =>
  `${customer?.first_name?.[0] || ''}${
    customer?.last_name?.[0] || ''
  }`.toUpperCase() || '?';

const Card = ({ title, action, children }) => (
  <section className="bg-white rounded-xl border border-[#E9E9E9]">
    <div className="flex justify-between items-center px-5 py-4 border-b border-[#E9E9E9]">
      <h2 className="text-[15px] font-semibold text-[#1F2937]">{title}</h2>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const InfoRow = ({ icon: Icon, label, value, copyable }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="flex gap-3 items-start py-3 first:pt-0 last:pb-0">
      <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-[#F3F4F6] text-[#6B7280]">
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-[#6B7280]">{label}</p>
        <p
          className={`text-[14px] font-medium truncate ${
            value ? 'text-[#1F2937]' : 'text-[#9CA3AF]'
          }`}
        >
          {value || 'Not provided'}
        </p>
      </div>
      {copyable && value && (
        <button
          onClick={copy}
          aria-label={`Copy ${label}`}
          className="p-1.5 rounded-md text-[#9CA3AF] hover:text-[#549A9A] hover:bg-[#F3F4F6]"
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  );
};

function CustomerDetails({ customer, address }) {
  const [showVerify, setShowVerify] = useState(false);
  const verified = Boolean(customer?.email_verified);
  const fullName =
    [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
    'Unnamed user';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 justify-between items-center p-6 bg-white rounded-xl border border-[#E9E9E9]">
        <div className="flex gap-4 items-center min-w-0">
          <div className="grid place-items-center w-16 h-16 shrink-0 rounded-full bg-[#549A9A] text-white text-[22px] font-semibold">
            {initials(customer)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap gap-2 items-center">
              <h1 className="text-[22px] font-semibold text-[#1F2937] capitalize truncate">
                {fullName}
              </h1>
              <span
                className={`inline-flex gap-1 items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium ${
                  verified
                    ? 'bg-[#ECFDF5] text-[#047857]'
                    : 'bg-[#FEF2F2] text-[#B91C1C]'
                }`}
              >
                {verified ? <BadgeCheck size={13} /> : <ShieldAlert size={13} />}
                {verified ? 'Verified' : 'Not verified'}
              </span>
            </div>
            <p className="mt-1 text-[14px] text-[#6B7280]">
              {customer?.username && `@${customer.username} · `}
              Joined {moment(customer?.created_at).format('LL')}
            </p>
          </div>
        </div>
        {!verified && (
          <button
            onClick={() => setShowVerify(true)}
            className="flex gap-2 items-center h-[42px] px-5 rounded-[10px] bg-[#549A9A] hover:bg-[#447f7f] text-[14px] font-semibold text-[#E2FFEE] transition-colors"
          >
            <BadgeCheck size={16} />
            Mark as verified
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Contact information">
          <div className="divide-y divide-[#F3F4F6]">
            <InfoRow icon={Mail} label="Email" value={customer?.email} copyable />
            <InfoRow
              icon={Phone}
              label="Phone number"
              value={customer?.phone}
              copyable
            />
            <InfoRow icon={AtSign} label="Username" value={customer?.username} />
            <InfoRow
              icon={CalendarDays}
              label="Joined"
              value={moment(customer?.created_at).format('LL')}
            />
          </div>
        </Card>

        <Card
          title="Addresses"
          action={
            address.length > 0 && (
              <span className="text-[13px] text-[#6B7280]">
                {address.length} saved
              </span>
            )
          }
        >
          {address.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="grid place-items-center w-11 h-11 rounded-full bg-[#F3F4F6] text-[#9CA3AF]">
                <MapPin size={20} />
              </div>
              <p className="mt-3 text-[14px] font-medium text-[#4B5563]">
                No address provided
              </p>
              <p className="mt-1 text-[13px] text-[#9CA3AF]">
                This user hasn't saved a delivery address yet.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {address.map((item) => (
                <li
                  key={item._id}
                  className="flex gap-3 items-start p-4 rounded-xl border border-[#E9E9E9]"
                >
                  <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-[#549A9A]/10 text-[#549A9A]">
                    <MapPin size={16} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-[#1F2937]">
                      {item.street_address || 'No street address'}
                    </p>
                    <p className="text-[13px] text-[#6B7280]">
                      {[item.city, item.state, item.country]
                        .filter(Boolean)
                        .join(', ') || 'Location not provided'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <ConfirmVerify
        id={customer._id}
        isOpen={showVerify}
        onOpenChange={setShowVerify}
      />
    </div>
  );
}

export default CustomerDetails;
